import { ChevronRight } from "lucide-react";
import posthog from "posthog-js";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { splitJobsByStatus } from "@/app/handlers/jobStatus";
import { slugifyURL } from "@/app/handlers/urlFunctions";
import { useCompanyJobs } from "@/app/services/create-resume/queries";
import JobListingItem, { JobListingItemJob } from "@/app/Views/JobsPage/components/JobListingItem";

/** Roles listed inline. Past this the section defers to the company page for the rest. */
const MAX_LISTED_ROLES = 3;

/** The company jobs endpoint carries the employer's own status flag alongside the listing fields. */
type CompanyRole = JobListingItemJob & { jobStatus?: string };

interface MoreRolesFromCompanyProps {
	companyId?: string;
	companyName?: string;
	/** Falls back to the company logo when a listing carries none of its own. */
	companyLogo?: string;
	/** The posting being read, kept out of its own list of other roles. */
	currentJobId?: string;
}

/**
 * Other roles the same employer is still hiring for, listed under the apply steps.
 *
 * Someone who reaches the bottom of a posting has either applied or decided against it. Both are
 * a good moment to show what else this employer has open, rather than sending them back to search.
 */
export function MoreRolesFromCompany({ companyId, companyName, companyLogo, currentJobId }: MoreRolesFromCompanyProps) {
	const navigate = useNavigate();
	// Same query key the company page and the row hover prefetch use, so this is usually a cache hit.
	const { data: response } = useCompanyJobs(companyId);

	const roles: CompanyRole[] = useMemo(() => {
		const jobs: CompanyRole[] = Array.isArray(response?.data) ? response.data : [];

		// Same open/closed rule the company page applies, so the two lists cannot disagree about
		// which roles are still live. A role with no company is dropped here rather than in the row:
		// JobListingItem renders nothing for one, which would leave the heading counting rows that
		// never appear.
		return splitJobsByStatus(jobs).openJobs.filter((job) => job._id !== currentJobId && Boolean(job.company));
	}, [response, currentJobId]);

	// Nothing worth a heading when this is the employer's only live posting.
	if (roles.length === 0) return null;

	const trackRoleClick = (job: CompanyRole, position: number) => {
		posthog.capture("company_more_roles_clicked", {
			jobId: job._id,
			jobTitle: job.jobTitle,
			companyId,
			companyName,
			sourceJobId: currentJobId,
			position,
		});
	};

	// The row title is already a link to the job, so only the CTA has to navigate itself.
	const openRole = (job: CompanyRole, position: number) => {
		trackRoleClick(job, position);

		const jobTitle = job.jobTitle;
		const company = job.company?.company_name || companyName;
		if (!job._id || !jobTitle || !company) return;

		navigate(`/jobs/${slugifyURL({ _id: job._id, companyName: company, jobTitle })}`);
		window.scrollTo(0, 0);
	};

	return (
		<section className="mb-8">
			<div className="py-3 border-b border-gray-200">
				<p className="text-base font-semibold font-inter text-gray-900">
					More open roles at {companyName || "this company"}
				</p>
				<p className="text-xs text-gray-500 hidden sm:block">Still accepting applications.</p>
			</div>

			<div>
				{roles.slice(0, MAX_LISTED_ROLES).map((job, index) => (
					<JobListingItem
						key={job._id}
						job={{ ...job, logo: job.logo || companyLogo || job.company?.logo_url }}
						// The employer's name is already the heading of this section.
						showCompany={false}
						titleClassName="text-sm font-semibold"
						onClick={() => trackRoleClick(job, index)}
						onApply={() => openRole(job, index)}
					/>
				))}
			</div>

			{/* Closes the list rather than competing with the rows: only offered once the section is
			    holding roles back, and secondary so it stays quieter than each row's own CTA. */}
			{roles.length > MAX_LISTED_ROLES && companyId && (
				// Centred by its own row: the button is inline-flex, so left alignment is what it
				// falls back to in normal flow. Full width on mobile, where centring is moot anyway.
				<div className="mt-4 flex justify-center">
					<Button asChild variant="secondary" className="w-full sm:w-auto">
						<Link to={`/companies/${companyId}`}>
							View all {roles.length} open roles
							<ChevronRight className="h-4 w-4 ml-2" />
						</Link>
					</Button>
				</div>
			)}
		</section>
	);
}
